import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { TID } from "../../lib/testIds";

const pains = [
    {
        title: "Disconnected systems",
        desc: "LMS here, CRM there, spreadsheets everywhere. Nothing talks to each other.",
    },
    {
        title: "Manual administration",
        desc: "Hours lost weekly to attendance, fee reconciliation, and ad-hoc reporting.",
    },
    {
        title: "Poor analytics",
        desc: "Decisions made on lagging gut-feel data, not real-time learning signals.",
    },
    {
        title: "Student tracking gaps",
        desc: "Drop-offs, struggling cohorts, and missed interventions go unseen.",
    },
    {
        title: "Inefficient assessments",
        desc: "Authoring, proctoring, grading and analytics live in separate worlds.",
    },
];

// Outer nodes (asymmetric layout) — fragmented tools around the unified center
const nodes = [
    { id: "lms", x: 20, y: 30, label: "LMS" },
    { id: "erp", x: 75, y: 20, label: "ERP" },
    { id: "sheets", x: 80, y: 70, label: "Sheets" },
    { id: "crm", x: 30, y: 80, label: "CRM" },
];

// Diagonal crossing connections
const lines = [
    ["lms", "sheets"],
    ["erp", "crm"],
    ["lms", "erp"],
    ["sheets", "crm"],
];

const Problem = () => (
    <section
        id="problem"
        data-testid={TID.problemSection}
        className="relative py-28 md:py-36 px-6"
    >
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* Left — fragmented graph */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-square w-full max-w-md mx-auto rounded-3xl border border-black/[0.06] bg-white shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:border-white/[0.06] dark:bg-white/[0.015] dark:shadow-none overflow-hidden"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-40 dark:opacity-60"
                    style={{
                        background:
                            "radial-gradient(circle at 70% 30%, rgba(139,92,246,0.15), transparent 60%)",
                    }}
                />

                <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    className="absolute inset-0 w-full h-full"
                >
                    {lines.map(([a, b], idx) => {
                        const n1 = nodes.find((n) => n.id === a);
                        const n2 = nodes.find((n) => n.id === b);
                        return (
                            <motion.line
                                key={idx}
                                x1={n1.x}
                                y1={n1.y}
                                x2={n2.x}
                                y2={n2.y}
                                stroke="url(#g1)"
                                strokeWidth="0.3"
                                strokeDasharray="1.5 1.5"
                                initial={{ pathLength: 0, opacity: 0 }}
                                whileInView={{ pathLength: 1, opacity: 0.6 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 1.4,
                                    delay: 0.2 + idx * 0.12,
                                    ease: "easeOut",
                                }}
                            />
                        );
                    })}
                    <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#8B5CF6" />
                            <stop offset="100%" stopColor="#22D3EE" />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Outer nodes */}
                {nodes.map((n, i) => (
                    <motion.div
                        key={n.id}
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{
                            duration: 0.6,
                            delay: 0.1 + i * 0.08,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${n.x}%`, top: `${n.y}%` }}
                    >
                        <div className="px-3 py-1.5 rounded-full bg-white/80 border border-black/[0.08] text-[10px] font-mono tracking-[0.2em] uppercase text-[#4B5563] backdrop-blur-sm shadow-sm dark:bg-white/[0.03] dark:border-white/10 dark:text-white/70 dark:shadow-none">
                            {n.label}
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            {/* Right — text */}
            <div>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="font-mono text-xs tracking-[0.4em] uppercase text-violet-500 dark:text-brand-blue/80"
                >
                    03 — The Problem
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.05 }}
                    className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-gradient-brand"
                >
                    Education runs on
                    <br />
                    fragmented tools.
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="mt-6 text-[#6B7280] dark:text-white/55 leading-relaxed max-w-lg"
                >
                    The world&apos;s institutions are stitched together by
                    spreadsheets, legacy ERPs and disconnected portals — at the
                    cost of clarity, time and student outcomes.
                </motion.p>

                <ul className="mt-10 space-y-4">
                    {pains.map((p, i) => (
                        <motion.li
                            key={p.title}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.6,
                                delay: 0.15 + i * 0.07,
                            }}
                            className="flex items-start gap-3"
                        >
                            <AlertTriangle
                                size={16}
                                className="text-violet-500 dark:text-brand-purple mt-1 shrink-0"
                            />
                            <div>
                                <p className="font-display text-base font-semibold text-[#111827] dark:text-white">
                                    {p.title}
                                </p>
                                <p className="text-sm text-[#6B7280] dark:text-white/50">
                                    {p.desc}
                                </p>
                            </div>
                        </motion.li>
                    ))}
                </ul>
            </div>
        </div>
    </section>
);

export default Problem;
